
import { supabase } from '@/integrations/supabase/client';

// Check if a category exists by slug
const checkCategoryExists = async (slug: string) => {
  const { data } = await supabase
    .from('categories')
    .select('id')
    .eq('slug', slug)
    .single();
  
  return !!data;
};

// Create a new category
const createCategory = async (name: string, slug: string) => {
  const exists = await checkCategoryExists(slug);
  
  if (!exists) {
    const { data, error } = await supabase
      .from('categories')
      .insert({ name, slug })
      .select()
      .single();
      
    if (error) {
      console.error('Error creating category:', error);
      return null;
    }
    
    console.log(`Created category: ${name}`);
    return data;
  } else {
    console.log(`Category ${name} already exists`);
    
    // Return the existing category
    const { data } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', slug)
      .single();
      
    return data;
  }
};

// Mock user data for sample listings
const mockUsers = [
  '9ec944e1-e9cb-4c6a-94b0-8f1bc9624f30', // replace with actual user IDs if available
  'a3e7f8c9-b2d1-4e6f-9a5b-8c7d6e5f4a3b',
  'd2c1b0a9-e8f7-4d6c-5b4a-3c2d1e0f9a8b'
];

// Generate random user ID
const getRandomUser = () => {
  const index = Math.floor(Math.random() * mockUsers.length);
  return mockUsers[index];
};

// Generate random price
const getRandomPrice = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min) * 100;
};

// Create sample listings
const createSampleListings = async (
  categoryId: number, 
  count: number, 
  options: {
    titlePrefix: string,
    minPrice: number,
    maxPrice: number,
    locations: string[],
    imageUrls: string[]
  }
) => {
  const { titlePrefix, minPrice, maxPrice, locations, imageUrls } = options;
  
  for (let i = 0; i < count; i++) {
    const title = `${titlePrefix} ${i + 1}`;
    const price = getRandomPrice(minPrice, maxPrice);
    const location = locations[Math.floor(Math.random() * locations.length)];
    const isPremium = Math.random() > 0.8; // 20% chance to be premium
    const deliveryOption = Math.random() > 0.3 ? 'shipping_and_pickup' : 'pickup_only'; // 70% chance to have shipping
    const shippingCost = deliveryOption === 'shipping_and_pickup' ? Math.floor(Math.random() * 10) + 2 : null; // Random shipping cost between 2-12 EUR
    
    // Create a random array of 1-3 images
    const imageCount = Math.floor(Math.random() * 3) + 1;
    const images = [];
    for (let j = 0; j < imageCount; j++) {
      const randomIndex = Math.floor(Math.random() * imageUrls.length);
      images.push(imageUrls[randomIndex]);
    }
    
    const { error } = await supabase
      .from('listings')
      .insert({
        title,
        description: `Ky është një shembull i një ${titlePrefix.toLowerCase()} të ofruar për shitje në platformën tonë. Produkti është në gjendje të mirë dhe çmimi është i negociueshëm.`,
        price,
        category_id: categoryId,
        user_id: getRandomUser(),
        location,
        images,
        is_premium: isPremium,
        is_sold: false,
        stock: Math.floor(Math.random() * 10) + 1, // Random stock between 1 and 10
        delivery_option: deliveryOption,
        shipping_cost: shippingCost
      });
      
    if (error) {
      console.error(`Error creating listing ${title}:`, error);
    } else {
      console.log(`Created listing: ${title}`);
    }
  }
};

// Main function to seed data
export const seedData = async () => {
  try {
    console.log('Starting data seeding...');
    
    // Change from const to let to allow reassignment
    let { data: categories, error } = await supabase
      .from('categories')
      .select('*');
      
    if (error) {
      throw error;
    }
    
    // If no categories exist, create default ones
    if (!categories || categories.length === 0) {
      console.log('No categories found. Creating some default categories...');
      await createCategory('Automjete', 'automjete');
      await createCategory('Elektronikë', 'elektronikë');
      await createCategory('Shtëpi & Mobilje', 'shtëpi');
      await createCategory('Modë & Aksesorë', 'veshje');
      await createCategory('Pasuri të Paluajtshme', 'pasuri');
      
      // Refetch categories after creating defaults
      const { data: updatedCategories, error: refetchError } = await supabase
        .from('categories')
        .select('*');
        
      if (refetchError) {
        throw refetchError;
      }
      
      // Now we can reassign categories since we used let
      categories = updatedCategories;
    }
    
    // Sample data for each category
    const sampleData = {
      'automjete': {
        titlePrefix: 'Automjet',
        minPrice: 30, 
        maxPrice: 300,
        locations: ['Tiranë', 'Durrës', 'Vlorë', 'Shkodër'],
        imageUrls: [
          'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2',
          'https://images.unsplash.com/photo-1583121274602-3e2820c69888',
          'https://images.unsplash.com/photo-1553440569-bcc63803a83d'
        ]
      },
      'shtëpi': {
        titlePrefix: 'Pajisje Shtëpiake',
        minPrice: 10, 
        maxPrice: 150,
        locations: ['Tiranë', 'Elbasan', 'Fier', 'Korçë'],
        imageUrls: [
          'https://images.unsplash.com/photo-1581092787765-e3feb951d987',
          'https://images.unsplash.com/photo-1585515320310-259814833e62',
          'https://images.unsplash.com/photo-1584889707286-31236bc659b6'
        ]
      },
      'elektronikë': {
        titlePrefix: 'Pajisje Elektronike',
        minPrice: 20, 
        maxPrice: 200,
        locations: ['Tiranë', 'Durrës', 'Vlorë', 'Gjirokastër'],
        imageUrls: [
          'https://images.unsplash.com/photo-1546868871-7041f2a55e12',
          'https://images.unsplash.com/photo-1593642702749-b7d2a804fbcf',
          'https://images.unsplash.com/photo-1498049794561-7780e7231661'
        ]
      },
      'mobilje': {
        titlePrefix: 'Mobilje',
        minPrice: 40, 
        maxPrice: 250,
        locations: ['Tiranë', 'Durrës', 'Lezhë', 'Berat'],
        imageUrls: [
          'https://images.unsplash.com/photo-1555041469-a586c61ea9bc',
          'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e',
          'https://images.unsplash.com/photo-1538688525198-9b88f6f53126'
        ]
      },
      'veshje': {
        titlePrefix: 'Veshje',
        minPrice: 5, 
        maxPrice: 100,
        locations: ['Tiranë', 'Shkodër', 'Pogradec', 'Sarandë'],
        imageUrls: [
          'https://images.unsplash.com/photo-1562157873-818bc0726f68',
          'https://images.unsplash.com/photo-1591047139829-d91aecb6caea',
          'https://images.unsplash.com/photo-1554568218-0f1715e72254'
        ]
      },
      'pasuri': {
        titlePrefix: 'Pronë Imobiliare',
        minPrice: 200, 
        maxPrice: 1500,
        locations: ['Tiranë', 'Durrës', 'Vlorë', 'Shkodër'],
        imageUrls: [
          'https://images.unsplash.com/photo-1564013799919-ab600027ffc6',
          'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2',
          'https://images.unsplash.com/photo-1512917774080-9991f1c4c750'
        ]
      },
    };
    
    // Create sample listings for each category
    for (const category of categories) {
      const data = sampleData[category.slug as keyof typeof sampleData];
      
      if (data) {
        // Count existing listings for this category
        const { count, error: countError } = await supabase
          .from('listings')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id);
          
        if (countError) {
          console.error(`Error counting listings for ${category.name}:`, countError);
          continue;
        }
        
        // Only create sample listings if there are fewer than 10
        if (count < 10) {
          const numToCreate = 10 - (count || 0);
          console.log(`Creating ${numToCreate} sample listings for ${category.name}`);
          await createSampleListings(category.id, numToCreate, data);
        } else {
          console.log(`Category ${category.name} already has enough listings`);
        }
      }
    }
    
    console.log('Data seeding completed successfully!');
    return true;
  } catch (error) {
    console.error('Error seeding data:', error);
    return false;
  }
};
